import React from 'react';
import { Button, Popover } from 'freemium-ui';
import copy from 'clipboard-copy';
import { useTranslation } from 'react-i18next';
import { FormServFieldType } from '../../../../../../shared/types';
import Icon from '../../../../../../shared/components/Icon';
import {
  HIDE_DELETE_CUSTOM_FIELDS,
  HIDE_EDIT_CUSTOM_FIELDS,
} from '../../../../../../shared/constants';
import { getText } from '../../../../../../shared/text';
import {
  ChoiceCard,
  ChoicesContainer,
  ChoicesOverflow,
  CustomFieldDisplay,
  CustomFieldListItem,
  CustomFieldMeta,
} from '../styles';

const {
  settings: { customfields },
} = getText('EN');

type FieldDisplayType = {
  field: FormServFieldType;
  onDeleteField: (field: FormServFieldType, skipConfirmation?: boolean) => void;
  isLoading?: boolean;
  setEditId: (editId: string) => void;
  editId?: string;
};

let timeout;

const FieldDisplay = (props: FieldDisplayType) => {
  const { field, onDeleteField, isLoading, setEditId, editId } = props;
  const [isCopied, setIsCopied] = React.useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<any>(null);
  const { t } = useTranslation();
  return (
    <CustomFieldListItem>
      <CustomFieldDisplay>
        <CustomFieldMeta>
          <div className="field-label" title={field.label || ''}>
            {field.field_options?.fs_default
              ? `${t(
                  `contacts.import_contact.default_fields.contact.${field?.name}`,
                )}`
              : field.label || ''}
            {field.required && <span className="required">*</span>}
          </div>
          {field.field_options?.fs_default && (
            <div className="default">
              {t(`${customfields?.fields?.default?.name}`)}
            </div>
          )}
          <div className="field-name">{field.name}</div>
          <div
            className={`field-copy ${isCopied ? '' : 'hide'}`.trim()}
            onClick={() => {
              setIsCopied(true);
              setTimeout(() => setIsCopied(false), 1000);
              copy(field.name);
            }}
          >
            <Icon width="12" height="12" glyph="copy" />
          </div>
          {isCopied && <div className="label-text">{t('copied')}</div>}
        </CustomFieldMeta>
        <div className="field-actions hide">
          {!editId &&
            field.editable &&
            !HIDE_EDIT_CUSTOM_FIELDS.includes(field.name) && (
              <Button
                size="mini"
                inline
                disabled={isLoading}
                type="secondary"
                onClick={() => {
                  setEditId(field.id);
                }}
              >
                <Icon fill="none" width="13" height="13" glyph="edit" />
                {t(`${customfields?.fields?.buttonEdit?.name}`)}
              </Button>
            )}
          {!field.field_options?.fs_default &&
            field.editable &&
            !HIDE_DELETE_CUSTOM_FIELDS.includes(field.name) && (
              <Button
                size="mini"
                inline
                disabled={isLoading}
                type="secondary"
                onClick={(event: React.MouseEvent) => {
                  event.preventDefault();
                  onDeleteField(field);
                }}
              >
                <Icon fill="none" width="12" height="14" glyph="delete" />
                {t(`${customfields?.fields?.buttonDelete?.name}`)}
              </Button>
            )}
        </div>
      </CustomFieldDisplay>
      {field.type === 2 && (field?.choices || []).length > 0 && (
        <ChoicesContainer>
          {field.choices?.slice(0, 4).map((choice) => (
            <ChoiceCard key={choice.id}>{choice.value}</ChoiceCard>
          ))}
          {(field.choices || []).length > 4 && (
            <>
              <ChoiceCard
                onMouseEnter={(e) => {
                  setAnchorEl(e.currentTarget);
                  timeout = setTimeout(() => {
                    setAnchorEl(null);
                  }, 3000);
                }}
                className="pointer"
                aria-describedby="choice-overflow-popup"
              >{`+ ${(field.choices?.length || 0) - 4}`}</ChoiceCard>
              <Popover
                id="choice-overflow-popup"
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                <ChoicesOverflow
                  onMouseEnter={() => {
                    clearTimeout(timeout);
                    timeout = undefined;
                  }}
                  onMouseLeave={() => setAnchorEl(null)}
                >
                  {field.choices?.slice(4).map((choice) => (
                    <div className="choice" key={choice.id}>
                      {choice.value}
                    </div>
                  ))}
                </ChoicesOverflow>
              </Popover>
            </>
          )}
        </ChoicesContainer>
      )}
    </CustomFieldListItem>
  );
};

export default FieldDisplay;
